/**
 * SendDocumentDialog — verzenddialoog voor Warehouse Document Distribution.
 *
 * Onderdelen:
 *   - Multi-select checkboxes van gekoppelde documenten
 *   - Template-picker (built-in + custom) met preview
 *   - To / CC / BCC (comma-separated + Enter)
 *   - Onderwerp + Body (HTML/tekst) met variabele-substitutie
 *   - Live preview van de rendered body
 *
 * Achterin roept dit naar POST /api/admin/warehouse-shipments/{sid}/documents/send
 * die de Document Distribution Service aanroept.
 *
 * P0/P1 follow-up (jun 2026): `documents` is nu de GECOMBINEERDE lijst uit
 * `ShipmentDocumentsTab` (canonical systeemdocumenten + canonical relaties +
 * expliciete links/uploads). Canonical items hebben geen link-record en gaan
 * als `document_ids` naar de backend — er wordt nooit een duplicaat-link
 * aangemaakt om te kunnen mailen.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Send, Loader2, X, FileText, ChevronDown, Eye, Code2,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../../ui/select";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import ContactPersonSelector from "../../shared/ContactPersonSelector";

function renderTemplate(str, ctx) {
  if (!str) return "";
  return str.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => (ctx?.[k] ?? `{{${k}}}`));
}

function parseCsvEmails(v) {
  if (!v) return [];
  return v.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
}

export default function SendDocumentDialog({
  shipment, documents, preselectedKeys = [], onClose, onSent, customsDocument = null,
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set(preselectedKeys));
  const [templates, setTemplates] = useState({ builtins: [], custom: [] });
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [customer, setCustomer] = useState(null);
  const [internalRecipients, setInternalRecipients] = useState([]);
  const [internalRecipientId, setInternalRecipientId] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactKey, setContactKey] = useState("primary");
  const [recipientError, setRecipientError] = useState("");
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const primaryRecipient = customer?.email || "";
  const customerName = customer?.display_name || customer?.company_name || "Klant";
  const trackingNumber = shipment?.tracking_number || "";

  const [to, setTo] = useState(primaryRecipient);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTo(""); setRecipientError(""); setRecipientsLoading(true);
    setSelectedContactId(null); setInternalRecipientId("");
    const fetchRecipients = async () => {
      try {
        if (customsDocument) {
          const { data } = await adminApi.get(`/admin/customs-invoices/${customsDocument.id}/recipients`);
          if (cancelled) return;
          setInternalRecipients(data.items || []);
          setSubject(`Douanefactuur ${customsDocument.invoice_number}`);
          setBody("Bijgevoegd de definitieve douanefactuur voor intern operationeel gebruik.");
          return;
        }
        if (!shipment?.customer_id) throw new Error("Koppel eerst de juiste klant aan deze zending.");
        const { data } = await adminApi.get(`/admin/customers/${shipment.customer_id}/autofill`);
        if (cancelled) return;
        setCustomer(data);
        const contacts = data.additional_contacts || [];
        const scid = shipment.selected_contact_id;
        const selected = scid && scid !== "primary" ? contacts.find((c) => c.id === scid) : { email: data.email };
        const invalid = (shipment.selected_contact_customer_id && shipment.selected_contact_customer_id !== shipment.customer_id)
          || (scid && !selected)
          || (shipment.selected_contact_email && shipment.selected_contact_email.toLowerCase() !== (selected?.email || "").toLowerCase());
        if (invalid) {
          setContactKey("__choose__");
          setRecipientError("De opgeslagen contactkeuze is verouderd. Selecteer opnieuw een contact van deze klant.");
        } else {
          setContactKey(scid && scid !== "primary" ? `contact-${scid}` : "primary");
          setTo(selected?.email || data.email || "");
        }
      } catch (e) { if (!cancelled) setRecipientError(e.response ? formatApiError(e) : e.message); }
      finally { if (!cancelled) setRecipientsLoading(false); }
    };
    fetchRecipients();
    return () => { cancelled = true; };
  }, [customsDocument, shipment]);

  const substitutionContext = useMemo(() => ({
    tracking_number: trackingNumber,
    customer_name: customerName,
    customer_email: primaryRecipient,
    company_name: customer?.company_name || "",
  }), [trackingNumber, customerName, primaryRecipient, customer]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/document-distribution/templates");
      setTemplates(r.data);
    } catch (e) {
      /* silent */
    }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Apply selected template
  useEffect(() => {
    if (!selectedTemplateId || customsDocument) return;
    const all = [...(templates.builtins || []), ...(templates.custom || [])];
    const tpl = all.find((t) => t.id === selectedTemplateId);
    if (tpl) {
      setSubject(renderTemplate(tpl.subject || "", substitutionContext));
      setBody(renderTemplate(tpl.body_html || tpl.body || "", substitutionContext));
    }
  }, [selectedTemplateId, templates, substitutionContext, customsDocument]);

  // Auto-select first template if preselected doc-type matches
  useEffect(() => {
    if (selectedTemplateId || templates.builtins.length === 0 || selectedIds.size === 0) return;
    const first = documents.find((d) => selectedIds.has(d.key));
    if (!first) return;
    const map = {
      warehouse_receipt: "__builtin_warehouse_receipt",
      packing_list: "__builtin_packing_list",
      value_invoice: "__builtin_value_invoice",
      manifest: "__builtin_manifest",
    };
    const tplId = map[first.docType];
    if (tplId && templates.builtins.find((t) => t.id === tplId)) {
      setSelectedTemplateId(tplId);
    }
  }, [selectedIds, templates, documents, selectedTemplateId]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (recipientError || recipientsLoading) return;
    if (customsDocument && !internalRecipientId) { toast.error("Selecteer expliciet een bevoegde interne ontvanger"); return; }
    const chosen = documents.filter((d) => selectedIds.has(d.key));
    if (chosen.length === 0) { toast.error("Selecteer minimaal 1 document"); return; }
    // Canonical documenten hebben geen link-record → die gaan als
    // `document_ids` mee. Er wordt nooit een duplicaat-link gemaakt.
    const link_ids = chosen.map((d) => d.linkId).filter(Boolean);
    const document_ids = chosen.map((d) => d.documentId).filter(Boolean);
    const to_list = parseCsvEmails(to);
    if (to_list.length === 0) { toast.error("Voer minimaal 1 ontvanger in"); return; }
    if (!subject.trim()) { toast.error("Onderwerp is verplicht"); return; }
    if (!body.trim()) { toast.error("Bericht is verplicht"); return; }
    setBusy(true);
    try {
      const payload = {
        link_ids,
        document_ids,
        to: to_list,
        cc: parseCsvEmails(cc) || null,
        bcc: parseCsvEmails(bcc) || null,
        subject: subject.trim(),
        body: body.trim(),
        template_id: selectedTemplateId || null,
        selected_contact_id: selectedContactId,
      };
      const r = await adminApi.post(
        customsDocument ? `/admin/customs-invoices/${customsDocument.id}/send`
          : `/admin/warehouse-shipments/${shipment.id}/documents/send`,
        customsDocument ? { recipient_user_id: internalRecipientId, to,
                            subject: subject.trim(), message: body.trim() } : payload,
      );
      if (r.data.status === "skipped_no_key") {
        toast.warning("Resend niet geconfigureerd — e-mail niet verstuurd (test-modus)");
      } else if (r.data.status === "sent" || r.data.status === "queued") {
        toast.success(`${chosen.length} document${chosen.length > 1 ? "en" : ""} verzonden naar ${to_list.join(", ")}`);
      } else {
        toast.warning(`Status: ${r.data.status}`);
      }
      onSent(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const allTemplates = [
    { id: "", name: "— Kies template — (of vul zelf in)" },
    ...(templates.builtins || []).map((t) => ({ ...t, section: "Standaard" })),
    ...(templates.custom || []).map((t) => ({ ...t, section: "Eigen" })),
  ];

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto"
                     data-testid="send-document-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" /> Document verzenden
          </DialogTitle>
          <DialogDescription>
            Verzend één of meerdere gekoppelde documenten via het Communicatiecentrum. Verzending wordt automatisch gelogd in de Warehouse Timeline én zichtbaar in de klantcommunicatie.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Documenten multi-select */}
          <div>
            <Label className="text-xs">Bijlagen ({selectedIds.size} geselecteerd)</Label>
            <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded p-2 space-y-1"
                 data-testid="send-attachments-list">
              {documents.length === 0 ? (
                <div className="text-xs text-slate-500 italic">Geen documenten om te versturen. Koppel eerst een document aan de zending.</div>
              ) : documents.map((doc) => (
                <label key={doc.key}
                       className={`flex items-start gap-2 text-xs p-1 rounded ${
                         doc.renderable === false
                           ? "opacity-50 cursor-not-allowed"
                           : "cursor-pointer hover:bg-slate-50"}`}
                       data-testid={`send-attachment-checkbox-${doc.key}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(doc.key)}
                    disabled={doc.renderable === false}
                    onChange={() => toggle(doc.key)}
                    className="mt-0.5"
                  />
                  <FileText className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-[#082c50] flex items-center gap-1.5 flex-wrap">
                      {doc.docTypeLabel}
                      {doc.kind === "system" && (
                        <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-800 rounded uppercase font-bold">Systeem</span>
                      )}
                      {doc.kind === "canonical" && (
                        <span className="text-[9px] px-1 py-0.5 bg-emerald-100 text-emerald-800 rounded uppercase font-bold">Automatisch</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {doc.number || doc.title}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Recipients */}
          {recipientError && <p role="alert" className="bg-amber-50 p-2 text-xs text-amber-900" data-testid="send-recipient-error">{recipientError}</p>}
          {!customsDocument && customer && <ContactPersonSelector customer={customer} value={contactKey} alwaysVisible
            disabled={busy || recipientsLoading} testIdPrefix="send-contact-selector"
            onSelect={(key, contact) => { setContactKey(key); setSelectedContactId(key === "primary" ? "primary" : key.replace(/^contact-/, "")); setTo(contact?.email || ""); setRecipientError(""); }} />}
          {customsDocument && <div>
            <Label htmlFor="send-internal-recipient">Bevoegde interne ontvanger</Label>
            <select id="send-internal-recipient" className="mt-1 w-full min-w-0 max-w-full rounded border border-slate-300 bg-white p-2 text-sm"
              value={internalRecipientId} disabled={busy || recipientsLoading} data-testid="send-internal-recipient"
              onChange={(e) => { setInternalRecipientId(e.target.value); setTo(internalRecipients.find((u) => u.id === e.target.value)?.email || ""); }}>
              <option value="" data-testid="send-internal-recipient-empty">Selecteer een bevoegde ontvanger</option>
              {internalRecipients.map((u) => <option key={u.id} value={u.id} data-testid={`send-internal-recipient-${u.id}`}>{u.email}</option>)}
            </select>
            <p className="mt-1 text-xs text-slate-600" data-testid="send-internal-only-notice">Intern operationeel document. Geen automatische klantontvanger.</p>
          </div>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Aan (To) *</Label>
              <Input
                value={to} readOnly={!!customsDocument} onChange={(e) => setTo(e.target.value)}
                placeholder="klant@example.com"
                className="text-xs mt-1" data-testid="send-to-input"
              />
            </div>
            <div>
              <Label className="text-xs">CC</Label>
              <Input
                value={cc} disabled={!!customsDocument} onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com" className="text-xs mt-1" data-testid="send-cc-input"
              />
            </div>
            <div>
              <Label className="text-xs">BCC</Label>
              <Input
                value={bcc} disabled={!!customsDocument} onChange={(e) => setBcc(e.target.value)}
                placeholder="bcc@5999cargo.nl" className="text-xs mt-1" data-testid="send-bcc-input"
              />
            </div>
          </div>

          {/* Template picker */}
          <div>
            <Label className="text-xs">Template</Label>
            <Select disabled={!!customsDocument} value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger className="mt-1 text-xs" data-testid="send-template-select">
                <SelectValue placeholder="Kies template" />
              </SelectTrigger>
              <SelectContent>
                {allTemplates.map((t) => (
                  <SelectItem key={t.id || "empty"} value={t.id || "__empty"}>
                    {t.section ? `[${t.section}] ` : ""}{t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div>
            <Label className="text-xs">Onderwerp *</Label>
            <Input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Bevestiging ontvangst zending W5999..."
              className="text-sm mt-1" data-testid="send-subject-input"
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Bericht *</Label>
              <button type="button" onClick={() => setShowPreview((p) => !p)}
                      className="text-[10px] text-blue-600 hover:underline inline-flex items-center gap-1"
                      data-testid="send-preview-toggle">
                {showPreview ? <Code2 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showPreview ? "Bewerken" : "Preview"}
              </button>
            </div>
            {showPreview ? (
              <div className="mt-1 border border-slate-200 rounded p-3 min-h-[140px] bg-white"
                   data-testid="send-preview">
                <iframe
                  srcDoc={body}
                  title="Preview"
                  sandbox=""
                  className="w-full min-h-[220px] border-0"
                />
              </div>
            ) : (
              <Textarea
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="HTML of tekst. Beschikbare variabelen: {{customer_name}}, {{tracking_number}}, {{company_name}}, {{customer_email}}"
                className="text-xs mt-1 font-mono"
                data-testid="send-body-input"
              />
            )}
            <p className="text-[10px] text-slate-500 mt-1">
              Beschikbare variabelen: <code>&#123;&#123;customer_name&#125;&#125;</code> · <code>&#123;&#123;tracking_number&#125;&#125;</code> · <code>&#123;&#123;company_name&#125;&#125;</code>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={handleSend} disabled={busy || selectedIds.size === 0 || recipientsLoading || !!recipientError || (!!customsDocument && !internalRecipientId)}
                  className="bg-[#0055FF] text-white hover:bg-[#0044CC]"
                  data-testid="send-doc-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Verzenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
